import {
  afterNextRender,
  Component,
  ErrorHandler,
  inject,
  input,
  isDevMode,
} from '@angular/core';
import {
  ConversationalChatBindWebsiteLinksService,
  ConversationalChatService,
} from '@conversational/chat-core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { DomService, ScriptInjectOn } from '@innogy/utils-dom';

@Component({
  selector: 'wl-conversational-chat-ui',
  standalone: true,
  templateUrl: './conversational-chat-ui.component.html',
})
export class ConversationalChatUiComponent {
  readonly #config = inject(ENVIRONMENT_CONFIG);
  readonly #conversationalChatService = inject(ConversationalChatService);
  readonly #bindLinksOnWebpageService = inject(
    ConversationalChatBindWebsiteLinksService
  );
  readonly #domService = inject(DomService);
  readonly #errorHandler = inject(ErrorHandler);

  readonly login = input<boolean>();

  readonly #logPrefix = 'Cognigy Chatbot - ';

  constructor() {
    afterNextRender({
      write: /* istanbul ignore next */ async () => {
        await this.initializeChatbotIfAllowed();
      },
    });
  }

  async initializeChatbotIfAllowed() {
    if (!this.#conversationalChatService.isAllowedToInitialize()) {
      return;
    }

    try {
      await this.#initializeCognigyChatbot();
      this.#conversationalChatService.initializeWebchat();
    } catch (error) {
      this.#errorHandler.handleError(
        new Error(this.#logPrefix + 'Failed to initialize Cognigy Chatbot:')
      );
    }
  }

  async #initializeCognigyChatbot() {
    if (isDevMode()) {
      console.info(this.#logPrefix + 'Initialize Cognigy Chatbot');
    }

    await this.#loadCognigyWebchatScript();

    this.#bindLinksOnWebpageService.bindLinksToWebchat(
      (invokedClass, webchatDelay) =>
        this.#conversationalChatService.initializeWebchat(
          invokedClass,
          webchatDelay
        )
    );
  }

  #loadCognigyWebchatScript(): Promise<void> {
    if (isDevMode()) {
      console.info(this.#logPrefix + 'Load Cognigy Webchat script');
    }

    return new Promise<void>((resolve, reject) => {
      this.#domService.injectScript({
        src: this.#config.cognigyChatbot.webchatScriptUrl,
        injectOnPlatform: ScriptInjectOn.CLIENT_ONLY,
        async: true,
        target: 'body',
        onLoad: () => {
          if (isDevMode()) {
            console.info(
              this.#logPrefix + 'Successfully loaded Cognigy Webchat script'
            );
          }
          resolve();
        },
        onError: () => {
          reject(
            new Error(this.#logPrefix + 'Failed to load Cognigy Webchat script')
          );
        },
      });
    });
  }
}
